import { useEffect, useState } from "react";
import { CurrentUser } from "../../types/User";
import { AuthContext } from "./AuthContext";
import { DateTime } from "luxon";
import { useLocation, useNavigate } from "react-router-dom";
import AccountService from "../../services/AccountService";
import { useAlertContext } from "../alert/AlertContext";
import LayerService from "../../services/LayerService";
import { Layer } from "../../types/Layer";
import ApiClientFactory from "../../services/ApiClientFactory";
import { LocalStorageKeys } from "../../types/LocalStorageKeys";

export interface AuthProps {
  children: React.ReactNode;
}

const getUserDetails = (): CurrentUser | undefined => {
  const user = localStorage.getItem(LocalStorageKeys.USER_DETAILS);
  if (user != null) {
    return JSON.parse(user) as CurrentUser;
  } else {
    return undefined;
  }
};

const getLayers = (): Layer[] => {
  const layers = localStorage.getItem(LocalStorageKeys.LAYERS);
  if (layers != null) {
    return JSON.parse(layers) as Layer[];
  } else {
    return [];
  }
};

export const anonPaths = [
  "/login",
  "/forgottenpassword",
  "/register",
  "/maintenance",
  "/boundaries",
  "/datasets",
  "/purchase-licence",
  "/os-poi",
  "/osm-poi",
  "/public-transport",
  "/uk-route-lines",
  "/naptan",
  "/os-highways",
  "/os-mrn",
  "/os-openroads",
  "/osm-roads",
  "/uk-census",
  "/os-codepoints",
  "/us-census",
  "/population-centroids",
  "/inrix-trip",
  "/inrix-volumes",
  "/inrix-speed-profiles",
  "/admin/login",
];

const AuthProvider = ({ children }: AuthProps) => {
  const [user, setUser] = useState<CurrentUser | undefined>(getUserDetails());
  const [layers, setLayers] = useState<Layer[]>(getLayers());
  const navigate = useNavigate();

  const location = useLocation();
  const { showAlert } = useAlertContext();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshGraceMinutes = 10;
  const tokenHeartbeatInterval = 60000;

  const checkTokenExpiry = () => {
    if (user && !isRefreshing) {
      if (user.tokenExpiry === undefined || user.refreshToken === undefined) {
        logout();
        return;
      }

      const expiry = DateTime.fromISO(user.tokenExpiry);
      if (expiry < DateTime.now()) {
        refreshToken(user, true);
      } else {
        const remainingMinutes = expiry.diffNow("minutes").minutes;
        if (remainingMinutes <= refreshGraceMinutes) {
          refreshToken(user, false);
        }
      }
    }
  };

  const refreshToken = (
    user: CurrentUser,
    requirePageRefreshOnSuccess: boolean,
  ) => {
    setIsRefreshing(true);

    let token = user.token;
    let refreshToken = user.refreshToken;
    let clientId = process.env.REACT_APP_CLIENT_ID;

    if (clientId === undefined) {
      return;
    }

    const client = ApiClientFactory.Create(user, logout, showAlert, () =>
      navigate("/maintenance"),
    );
    const accountService = new AccountService(client);
    accountService
      .RefreshToken(token, refreshToken, clientId)
      .then((res) => {
        setUser({
          ...user,
          token: res.token,
          tokenExpiry: res.tokenExpiry,
          refreshToken: res.refreshToken,
        });

        if (requirePageRefreshOnSuccess) {
          window.location.reload();
        }
      })
      .catch(() => {
        logout();
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  };

  const login = (user: CurrentUser) => {
    setUser(user);
    localStorage.setItem(LocalStorageKeys.USER_DETAILS, JSON.stringify(user));
  };

  const logout = (redirectBack?: boolean) => {
    if (!user) return;

    const client = ApiClientFactory.Create(user, logout, showAlert, () =>
      navigate("/maintenance"),
    );
    const accountService = new AccountService(client);
    accountService
      .Logout()
      .catch(() => {})
      .finally(() => {
        setUser(undefined);
        // Clear down local storage keys. These will be reloaded on login
        localStorage.removeItem(LocalStorageKeys.USER_DETAILS);
        localStorage.removeItem(LocalStorageKeys.FEATURE_TYPES);
        localStorage.removeItem(LocalStorageKeys.CURRENCIES);
        localStorage.removeItem(LocalStorageKeys.BOUNDARIES);
        localStorage.removeItem(LocalStorageKeys.LAYERS);
        if (redirectBack || redirectBack === undefined) {
          const redirectTarget =
            location.pathname.toLowerCase().startsWith("/maintenance") ?
              ""
            : `?redirect=${location.pathname}`;
          navigate(`/login${redirectTarget}`);
        } else {
          navigate("login");
        }
      });
  };

  useEffect(() => {
    checkTokenExpiry();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (!isRefreshing) {
      interval = setInterval(checkTokenExpiry, tokenHeartbeatInterval);
    }
    return () => {
      clearInterval(interval);
    };
  }, [user, isRefreshing]);

  useEffect(() => {
    if (
      !user &&
      location.pathname !== "/" &&
      !anonPaths.some((path) =>
        location.pathname.toLowerCase().startsWith(path),
      )
    ) {
      navigate(`/login?redirect=${location.pathname}`);
    }
  }, [user, navigate, location]);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
export { getUserDetails };
